import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { runAiJob, downloadBlob, formatApiError } from "../lib/api";
import { Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Plus, UploadSimple, Trash, Sparkle, FileXls, FilePdf, ArrowLeft, FileText, Robot,
  WarningCircle, Ticket, PlusCircle,
} from "@phosphor-icons/react";

const ROLES = [
  { key: "current_std", label: "Current / previous standard", hint: "Optional — the version enforceable today. Leave blank for a brand-new standard or a new standard family." },
  { key: "proposed_std", label: "Proposed / new standard", hint: "Required — the proposed new version (or the brand-new standard) under development." },
  { key: "supporting", label: "Supporting technical documents", hint: "Optional — anything that helps the analysis (specs, drawings, notes)." },
];
const PRI_CLS = {
  High: "bg-red-100 text-red-800 border-red-300",
  Medium: "bg-amber-100 text-amber-800 border-amber-300",
  Low: "bg-slate-100 text-slate-700 border-slate-300",
};
const DISP_CLS = {
  New: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Revised: "bg-amber-100 text-amber-800 border-amber-300",
  Retired: "bg-red-100 text-red-800 border-red-300",
  Unchanged: "bg-slate-100 text-slate-600 border-slate-300",
};

const DEVSTAR_KIND_LABEL = {
  purpose: "Purpose", applicability: "Applicability", requirement: "Requirement",
  subpart: "Sub-part", measure: "Measure", evidence_retention: "Evidence Retention",
  attachment: "Attachment",
};

function matchChallenge(item, kind, area) {
  const a = (area || "").toLowerCase().trim();
  const it = (item || "").toLowerCase().trim();
  if (!a) return false;
  if (kind === "requirement" || kind === "subpart" || kind === "measure") {
    if (!it) return false;
    const esc = it.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(?<![\\w.])${esc}(?![\\w.])`).test(a);
  }
  if (kind === "purpose") return a.includes("purpose");
  if (kind === "applicability") return a.includes("applicab");
  if (kind === "evidence_retention") return a.includes("evidence") && a.includes("reten");
  if (kind === "attachment") return a.includes("attach") || a.includes("appendix");
  return false;
}

function buildUnifiedRows(breakdown, challenges) {
  const bd = breakdown || [], chs = challenges || [], used = new Set(), rows = [];
  for (const r of bd) {
    const matched = chs.filter((c) => matchChallenge(r.item, r.kind, c.area));
    if (matched.length) matched.forEach((c) => { used.add(c.id); rows.push({ bd: r, ch: c }); });
    else rows.push({ bd: r, ch: null });
  }
  for (const c of chs) if (!used.has(c.id)) rows.push({ bd: null, ch: c });
  return rows;
}

export default function SdpDevstar({ projectId }) {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null); // full devstar
  const [users, setUsers] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [subtab, setSubtab] = useState("summary");
  const fileRefs = useRef({});

  const loadList = async () => {
    try { const { data } = await api.get(`/sdp/projects/${projectId}/devstars`); setList(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load DevSTaR list."); }
  };
  const openOne = async (id) => {
    try { const { data } = await api.get(`/sdp/devstars/${id}`); setActive(data); setDirty(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    loadList();
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, [projectId]);

  const create = async () => {
    try {
      const { data } = await api.post(`/sdp/projects/${projectId}/devstars`, { name: "" });
      toast.success("DevSTaR created.");
      await loadList(); openOne(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this DevSTaR evaluation? This cannot be undone.")) return;
    try { await api.delete(`/sdp/devstars/${id}`); toast.success("Deleted."); setActive(null); loadList(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upload = async (role, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("role", role); fd.append("file", file);
    try {
      const { data } = await api.post(`/sdp/devstars/${active.id}/sources`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data?.markings?.length) {
        toast.warning(`Heads up: "${file.name}" appears to carry private markings (${data.markings.join(", ")}). Confirm you intend to upload a confidential document.`, { duration: 8000 });
      } else {
        toast.success(`Uploaded ${file.name}`);
      }
      openOne(active.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRefs.current[role]) fileRefs.current[role].value = ""; }
  };
  const removeSource = async (sid) => {
    try { await api.delete(`/sdp/devstars/${active.id}/sources/${sid}`); openOne(active.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const hasRole = (r) => (active?.sources || []).some((s) => s.role === r);
  const canAnalyze = active && hasRole("proposed_std") && !analyzing;
  const flaggedSources = (active?.sources || []).filter((s) => s.markings?.length);

  const analyze = async () => {
    if (!canAnalyze) return;
    setAnalyzing(true);
    toast.info("Running AI evaluation — this uses 1 AI credit and may take up to a minute.");
    try {
      await runAiJob(`/sdp/devstars/${active.id}/analyze`, {}, {}, { maxTries: 90 });
      toast.success("Analysis complete.");
      await openOne(active.id); await loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed."); }
    finally { setAnalyzing(false); }
  };

  const addChallenge = async () => {
    try {
      await api.post(`/sdp/devstars/${active.id}/challenges`, { challenge: "New challenge", priority: "Medium" });
      openOne(active.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteChallenge = async (cid) => {
    try { await api.delete(`/sdp/devstars/${active.id}/challenges/${cid}`); openOne(active.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pushToFlow = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/sdp/devstars/${active.id}/push-to-flow`, { assignee: assignee || undefined });
      if (data.pushed > 0) toast.success(`Created ${data.pushed} ClaroFlow ticket(s).`);
      else toast.info("No new tickets — all challenges are already tracked.");
      openOne(active.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPushing(false); }
  };

  const save = async () => {
    try {
      await api.patch(`/sdp/devstars/${active.id}`, {
        name: active.name, executive_summary: active.executive_summary,
        challenges: (active.challenges || []).map((c) => ({
          id: c.id, notes: c.notes, action_items: c.action_items, priority: c.priority,
          challenge: c.challenge, area: c.area, impact: c.impact, recommendation: c.recommendation,
        })),
        breakout: (active.breakout || []).map((r) => ({
          id: r.id, disposition: r.disposition, difference: r.difference, smes: r.smes,
          current_policies: r.current_policies, controls_text: r.controls_text, action_items: r.action_items,
        })),
        breakdown: (active.breakdown || []).map((r) => ({
          id: r.id, smes: r.smes, current_policies: r.current_policies,
          controls_text: r.controls_text, action_items: r.action_items,
        })),
      });
      toast.success("Saved."); setDirty(false); loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const patchChallenge = (id, field, val) => {
    setActive((a) => ({ ...a, challenges: a.challenges.map((c) => (c.id === id ? { ...c, [field]: val } : c)) }));
    setDirty(true);
  };
  const patchBreakout = (id, field, val) => {
    setActive((a) => ({ ...a, breakout: a.breakout.map((r) => (r.id === id ? { ...r, [field]: val } : r)) }));
    setDirty(true);
  };
  const patchBreakdown = (id, field, val) => {
    setActive((a) => ({ ...a, breakdown: a.breakdown.map((r) => (r.id === id ? { ...r, [field]: val } : r)) }));
    setDirty(true);
  };
  const download = (endpoint, filename) => downloadBlob(`/sdp/devstars/${active.id}/${endpoint}`, filename)
    .catch((e) => toast.error(e.message || "Download failed."));

  // -------- List view --------
  if (!active) {
    return (
      <div data-testid="devstar-list" className="my-3">
        <div className="flex justify-between items-center mb-3">
          <p className="text-sm text-muted-foreground max-w-2xl">
            <span className="font-semibold text-foreground">DevSTaR — Development Standard Tracking and Review form.</span> Upload the current standard and the proposed changes, then let AI surface the challenges the change
            creates for your program — and how to address them. Add any supporting technical documents for context.
          </p>
          <Button size="sm" className="rounded-none shrink-0" data-testid="devstar-new-btn" onClick={create}>
            <Plus size={15} className="mr-1" />New DevSTaR
          </Button>
        </div>
        {list.length === 0 ? (
          <Empty title="No DevSTaR evaluations yet" hint="Create one to compare a current vs proposed standard with AI." />
        ) : (
          <div className="border border-border divide-y divide-border">
            {list.map((d) => (
              <button key={d.id} data-testid={`devstar-row-${d.id}`} onClick={() => openOne(d.id)}
                className="w-full text-left p-3 hover:bg-secondary/40 flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <FileText size={16} className="text-[#071B3E]" />{d.name}
                </span>
                <span className={`text-[11px] px-2 py-0.5 border ${d.status === "ready" ? "bg-emerald-50 text-emerald-800 border-emerald-300" : d.status === "analyzing" ? "bg-blue-50 text-blue-800 border-blue-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                  {d.status === "ready" ? "Analyzed" : d.status === "analyzing" ? "Analyzing…" : "Draft"}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // -------- Detail view --------
  return (
    <div data-testid="devstar-detail" className="my-3">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => { setActive(null); loadList(); }} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="devstar-back-btn">
          <ArrowLeft size={14} />All DevSTaRs
        </button>
        <div className="flex gap-2">
          {active.status === "ready" && <>
            <Button size="sm" variant="outline" className="rounded-none" data-testid="devstar-export-xlsx" onClick={() => download("export.xlsx", "DevSTaR.xlsx")}><FileXls size={15} className="mr-1" />Excel</Button>
            <Button size="sm" variant="outline" className="rounded-none" data-testid="devstar-export-pdf" onClick={() => download("export.pdf", "DevSTaR.pdf")}><FilePdf size={15} className="mr-1" />PDF</Button>
          </>}
          <Button size="sm" variant="outline" className="rounded-none text-red-700 border-red-300" data-testid="devstar-delete-btn" onClick={() => remove(active.id)}><Trash size={15} /></Button>
        </div>
      </div>

      <Input data-testid="devstar-name" value={active.name} onChange={(e) => { setActive({ ...active, name: e.target.value }); setDirty(true); }}
        className="rounded-none text-base font-semibold mb-4" placeholder="DevSTaR name" />

      {flaggedSources.length > 0 && (
        <div data-testid="devstar-confidential-warning" className="flex items-start gap-2 mb-4 text-sm text-amber-900 bg-amber-50 border border-amber-300 px-3 py-2">
          <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
          <div>
            <b>Possible confidential document(s) uploaded.</b> {flaggedSources.map((s) => `"${s.filename}" (${s.markings.join(", ")})`).join("; ")}.
            {" "}Not everything needs to be private — but if these carry internal markings, confirm you intend to share them with AI, or remove them.
          </div>
        </div>
      )}

      {/* Upload zones */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {ROLES.map((r) => {
          const files = (active.sources || []).filter((s) => s.role === r.key);
          const required = r.key === "proposed_std";
          return (
            <div key={r.key} data-testid={`devstar-zone-${r.key}`} className="border border-border p-3 flex flex-col">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#071B3E] flex items-center gap-1">
                {r.label}{required && <span className="text-red-500">*</span>}
              </div>
              <div className="text-[11px] text-muted-foreground mb-2">{r.hint}</div>
              <div className="space-y-1 mb-2 flex-1">
                {files.length === 0 ? <div className="text-[11px] text-muted-foreground italic">No file yet.</div> :
                  files.map((s) => (
                    <div key={s.id} className="text-xs bg-secondary/40 border border-border px-2 py-1">
                      <div className="flex items-center justify-between">
                        <span className="truncate">{s.filename} {s.chars === 0 && <span className="text-amber-600">(no text found)</span>}</span>
                        <button onClick={() => removeSource(s.id)} className="text-slate-400 hover:text-red-600 ml-2 shrink-0"><Trash size={12} /></button>
                      </div>
                      {s.markings?.length > 0 && (
                        <div className="mt-1 flex items-center gap-1 text-[10px] text-amber-800">
                          <WarningCircle size={11} weight="fill" />{s.markings.join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              <label className="cursor-pointer">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#071B3E] hover:underline">
                  <UploadSimple size={14} />Upload PDF / Word / Excel
                </span>
                <input ref={(el) => (fileRefs.current[r.key] = el)} type="file" accept=".pdf,.docx,.xlsx" className="hidden"
                  disabled={busy} onChange={(e) => upload(r.key, e.target.files?.[0])} />
              </label>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-5">
        <Button className="rounded-none" data-testid="devstar-analyze-btn" onClick={analyze} disabled={!canAnalyze}>
          <Sparkle size={16} className="mr-1" weight="fill" />
          {analyzing ? "Analyzing…" : active.status === "ready" ? "Re-run AI analysis" : "Run AI analysis"}
        </Button>
        {!hasRole("proposed_std") ?
          <span className="text-xs text-muted-foreground">Upload the proposed / new standard to enable analysis (uses 1 AI credit). A current/previous standard is optional.</span> :
          <span className="text-xs text-muted-foreground">Uses 1 AI credit.</span>}
      </div>

      {analyzing && (
        <div data-testid="devstar-analyzing" className="flex items-center gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-200 px-3 py-2 mb-4">
          <Robot size={18} className="animate-pulse" />AI is comparing the standards and drafting the challenge analysis…
        </div>
      )}

      {active.status === "ready" && (
        <div className="space-y-4" data-testid="devstar-results">
          <div className="flex gap-4 border-b border-border text-sm">
            <button data-testid="devstar-subtab-summary" onClick={() => setSubtab("summary")}
              className={`pb-2 -mb-px border-b-2 ${subtab === "summary" ? "border-[#071B3E] font-semibold text-[#071B3E]" : "border-transparent text-muted-foreground"}`}>Summary & Challenges</button>
            <button data-testid="devstar-subtab-breakout" onClick={() => setSubtab("breakout")}
              className={`pb-2 -mb-px border-b-2 ${subtab === "breakout" ? "border-[#071B3E] font-semibold text-[#071B3E]" : "border-transparent text-muted-foreground"}`}>Standard Breakdown</button>
          </div>

          {subtab === "summary" && (<>
          <div>
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Executive Summary</h4>
              {dirty && <Button size="sm" className="rounded-none h-7" data-testid="devstar-save-btn" onClick={save}>Save changes</Button>}
            </div>
            <Textarea data-testid="devstar-summary" value={active.executive_summary || ""} rows={4} className="rounded-none text-sm"
              onChange={(e) => { setActive({ ...active, executive_summary: e.target.value }); setDirty(true); }} />
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Challenges & Recommendations ({active.challenges?.length || 0})</h4>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-none h-8" data-testid="devstar-add-challenge" onClick={addChallenge}>
                  <PlusCircle size={14} className="mr-1" />Add challenge
                </Button>
                <Select value={assignee} onValueChange={setAssignee}>
                  <SelectTrigger className="rounded-none h-8 w-44 text-xs" data-testid="devstar-assignee"><SelectValue placeholder="Assign tickets to…" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="rounded-none h-8" data-testid="devstar-push-flow" onClick={pushToFlow} disabled={pushing || !(active.challenges || []).length}>
                  <Ticket size={14} className="mr-1" />{pushing ? "Pushing…" : "Push to ClaroFlow"}
                </Button>
              </div>
            </div>
            {(active.challenges || []).length === 0 && (active.breakdown || []).length === 0 ? (
              <Empty title="No analysis yet" hint="Re-run the AI analysis, or add a challenge manually." />
            ) : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm" data-testid="devstar-challenge-table">
                  <thead className="bg-[#071B3E] text-white">
                    <tr className="text-left">
                      <th className="p-2 w-8">#</th><th className="p-2 w-16">Item</th>
                      <th className="p-2 w-24">Type</th><th className="p-2 w-28">Functional Reg.</th>
                      <th className="p-2 w-14">VRF</th><th className="p-2 w-24">Time Horizon</th>
                      <th className="p-2 w-24">Disposition</th>
                      <th className="p-2 min-w-[20rem]">Standard Language</th>
                      <th className="p-2 min-w-[16rem]">Challenge</th><th className="p-2 w-24">Priority</th>
                      <th className="p-2">Impact</th><th className="p-2">Recommended approach</th>
                      <th className="p-2 w-56">SME notes & actions</th>
                      <th className="p-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildUnifiedRows(active.breakdown, active.challenges).map((row, i) => {
                      const { bd, ch } = row;
                      const manual = ch && ch.source === "manual";
                      const isReq = bd && bd.kind === "requirement";
                      const isHead = bd && ["purpose", "applicability", "evidence_retention", "attachment"].includes(bd.kind);
                      const rowCls = isReq ? "bg-[#DCE7F5]" : bd && bd.kind === "measure" ? "bg-[#EFF6EA]" : isHead ? "bg-[#EEF2F8]" : "";
                      return (
                      <tr key={(bd?.id || "g") + "-" + (ch?.id || i)} data-testid={`devstar-summary-row-${i}`} className={`border-t border-border align-top ${rowCls}`}>
                        <td className="p-2 text-muted-foreground text-xs">{i + 1}{manual && <div className="text-[9px] text-[#071B3E] font-semibold">MANUAL</div>}</td>
                        <td className={`p-2 text-xs whitespace-nowrap ${isReq || isHead ? "font-bold" : "font-medium"}`}>{bd ? bd.item : "—"}</td>
                        <td className="p-2 text-[11px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">{bd ? (DEVSTAR_KIND_LABEL[bd.kind] || bd.kind) : "General"}</td>
                        <td className="p-2 text-xs">{bd ? (bd.functions || "—") : "—"}</td>
                        <td className="p-2 text-xs">{bd ? (bd.vrf || "—") : "—"}</td>
                        <td className="p-2 text-xs">{bd ? (bd.time_horizon || "—") : "—"}</td>
                        <td className="p-2 text-xs">{bd && bd.disposition ? <span className={`text-[11px] px-1.5 py-0.5 border rounded-none ${DISP_CLS[bd.disposition] || ""}`}>{bd.disposition}</span> : "—"}</td>
                        <td className="p-2 text-xs whitespace-pre-wrap">{bd ? bd.language : ""}</td>
                        <td className="p-2 text-xs font-medium">
                          {!ch ? <span className="text-muted-foreground">—</span> : manual ? <Textarea data-testid={`devstar-edit-challenge-${ch.id}`} value={ch.challenge || ""} rows={2} className="rounded-none text-xs" onChange={(e) => patchChallenge(ch.id, "challenge", e.target.value)} /> : ch.challenge}
                        </td>
                        <td className="p-2">
                          {ch ? <select data-testid={`devstar-priority-${ch.id}`} value={ch.priority || "Medium"} onChange={(e) => patchChallenge(ch.id, "priority", e.target.value)}
                            className={`text-[11px] px-1.5 py-1 border rounded-none ${PRI_CLS[ch.priority] || PRI_CLS.Medium}`}>
                            <option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option>
                          </select> : "—"}
                        </td>
                        <td className="p-2 text-xs">
                          {!ch ? "" : manual ? <Textarea value={ch.impact || ""} rows={2} className="rounded-none text-xs" onChange={(e) => patchChallenge(ch.id, "impact", e.target.value)} /> : ch.impact}
                        </td>
                        <td className="p-2 text-xs">
                          {!ch ? "" : manual ? <Textarea value={ch.recommendation || ""} rows={2} className="rounded-none text-xs" onChange={(e) => patchChallenge(ch.id, "recommendation", e.target.value)} /> : ch.recommendation}
                        </td>
                        <td className="p-2">
                          {ch ? <>
                            <Textarea data-testid={`devstar-notes-${ch.id}`} value={ch.notes || ""} rows={2} placeholder="Your notes…" className="rounded-none text-xs mb-1" onChange={(e) => patchChallenge(ch.id, "notes", e.target.value)} />
                            <Textarea data-testid={`devstar-actions-${ch.id}`} value={ch.action_items || ""} rows={2} placeholder="Action items…" className="rounded-none text-xs" onChange={(e) => patchChallenge(ch.id, "action_items", e.target.value)} />
                          </> : <span className="text-xs text-muted-foreground">Edit SME columns on the Standard Breakdown tab</span>}
                        </td>
                        <td className="p-2 text-center">
                          {ch && ch.flow_ticket_id ?
                            <button onClick={() => nav("/claroflow")} data-testid={`devstar-tracked-${ch.id}`} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200">
                              <Ticket size={10} weight="fill" />Tracked
                            </button> : (ch ? <span className="text-[10px] text-muted-foreground">—</span> : null)}
                          {ch && <button onClick={() => deleteChallenge(ch.id)} data-testid={`devstar-del-challenge-${ch.id}`} className="block mx-auto mt-1.5 text-slate-400 hover:text-red-600"><Trash size={12} /></button>}
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            )}
            {dirty && <div className="mt-2 flex justify-end"><Button size="sm" className="rounded-none" data-testid="devstar-save-btn-2" onClick={save}>Save changes</Button></div>}
          </div>
          </>)}

          {subtab === "breakout" && (
            <div data-testid="devstar-breakout">
              <div className="flex items-center justify-between mb-2 gap-3">
                <p className="text-xs text-muted-foreground max-w-2xl">The drafted standard broken down in document flow — Purpose, Applicability, each Requirement with its sub-parts and Measure, Evidence Retention, and Attachments. Add SME notes on any row, then export to Excel.</p>
                <Button size="sm" variant="outline" className="rounded-none shrink-0" data-testid="devstar-breakout-xlsx" onClick={() => download("breakout.xlsx", "DevSTaR_Breakdown.xlsx")}><FileXls size={15} className="mr-1" />Excel</Button>
              </div>
              {(active.breakdown || []).length === 0 ? (
                <Empty title="No breakdown yet" hint="Re-run the AI analysis to parse the drafted standard into document flow." />
              ) : (
                <div className="border border-border overflow-x-auto">
                  <table className="w-full text-sm" data-testid="devstar-breakout-table">
                    <thead className="bg-[#071B3E] text-white">
                      <tr className="text-left">
                        <th className="p-2 w-20">Item</th><th className="p-2 w-24">Type</th>
                        <th className="p-2 w-32">Functional Reg.</th><th className="p-2 w-14">VRF</th>
                        <th className="p-2 w-28">Time Horizon</th><th className="p-2 w-24">Disposition</th>
                        <th className="p-2 min-w-[24rem]">Standard Language</th>
                        <th className="p-2 w-36">SMEs</th><th className="p-2 w-40">Current policies</th>
                        <th className="p-2 w-40">Controls</th><th className="p-2 w-40">Action items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.breakdown.map((r) => {
                        const isReq = r.kind === "requirement";
                        const isHead = ["purpose", "applicability", "evidence_retention", "attachment"].includes(r.kind);
                        const rowCls = isReq ? "bg-[#DCE7F5]" : r.kind === "measure" ? "bg-[#EFF6EA]" : isHead ? "bg-[#EEF2F8]" : "";
                        return (
                          <tr key={r.id} data-testid={`devstar-bd-${r.id}`} className={`border-t border-border align-top ${rowCls}`}>
                            <td className={`p-2 text-xs whitespace-nowrap ${isReq || isHead ? "font-bold" : "font-medium"}`}>{r.item}</td>
                            <td className="p-2 text-[11px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">{DEVSTAR_KIND_LABEL[r.kind] || r.kind}</td>
                            <td className="p-2 text-xs">{r.functions || "—"}</td>
                            <td className="p-2 text-xs">{r.vrf || "—"}</td>
                            <td className="p-2 text-xs">{r.time_horizon || "—"}</td>
                            <td className="p-2 text-xs">{r.disposition ? <span className={`text-[11px] px-1.5 py-0.5 border rounded-none ${DISP_CLS[r.disposition] || ""}`}>{r.disposition}</span> : "—"}</td>
                            <td className="p-2 text-xs whitespace-pre-wrap">{r.language}</td>
                            <td className="p-1"><Textarea value={r.smes || ""} rows={2} placeholder="Owners…" className="rounded-none text-xs" onChange={(e) => patchBreakdown(r.id, "smes", e.target.value)} /></td>
                            <td className="p-1"><Textarea value={r.current_policies || ""} rows={2} placeholder="Docs affected…" className="rounded-none text-xs" onChange={(e) => patchBreakdown(r.id, "current_policies", e.target.value)} /></td>
                            <td className="p-1"><Textarea value={r.controls_text || ""} rows={2} className="rounded-none text-xs" onChange={(e) => patchBreakdown(r.id, "controls_text", e.target.value)} /></td>
                            <td className="p-1"><Textarea value={r.action_items || ""} rows={2} className="rounded-none text-xs" onChange={(e) => patchBreakdown(r.id, "action_items", e.target.value)} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {dirty && <div className="mt-2 flex justify-end"><Button size="sm" className="rounded-none" onClick={save}>Save changes</Button></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
